import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Dropdown from './Dropdown';

const DataTable = ({
  columns = [],
  data = [],
  variant = 'default',
  pagination = false,
  pageSize = 10,
  sortable = false,
  filterable = false,
  selectable = false,
  onRowClick,
  onSelectionChange,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  ...props
}) => {
  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);
  
  // State for filtering
  const [filters, setFilters] = useState({});
  const [filteredData, setFilteredData] = useState(data);
  
  // State for selection
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Update filtered data when data or filters change
  useEffect(() => {
    let result = [...data];
    
    // Apply filters
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        result = result.filter(item => {
          const value = item[key];
          return filters[key].includes(value);
        });
      }
    });
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredData(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [data, filters, sortConfig]);
  
  // Update selection callback
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows, onSelectionChange]);
  
  // Request sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Get sort direction icon
  const getSortDirectionIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };
  
  // Handle row selection
  const handleSelectRow = (id, checked) => {
    if (checked) {
      setSelectedRows([...selectedRows, id]);
    } else {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    }
  };
  
  // Handle select all rows
  const handleSelectAllRows = (checked) => {
    if (checked) {
      const allIds = paginatedData.map(row => row.id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (key, values) => {
    setFilters(prev => ({
      ...prev,
      [key]: values
    }));
  };
  
  // Get unique values for a column (for filtering)
  const getUniqueValues = (key) => {
    const uniqueValues = [...new Set(data.map(item => item[key]))];
    return uniqueValues.map(value => ({
      label: value.toString(),
      value: value
    }));
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = pagination ? filteredData.slice(startIndex, endIndex) : filteredData;
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Handle page size change
  const handlePageSizeChange = (size) => {
    setItemsPerPage(parseInt(size));
    setCurrentPage(1); // Reset to first page when page size changes
  };
  
  // Determine table variant classes
  const tableClasses = {
    default: 'min-w-full divide-y divide-border',
    compact: 'min-w-full divide-y divide-border text-sm',
  };
  
  // Determine if all rows on current page are selected
  const allSelected = paginatedData.length > 0 && 
    paginatedData.every(row => selectedRows.includes(row.id));
  
  // Determine if some rows on current page are selected
  const someSelected = paginatedData.some(row => selectedRows.includes(row.id)) && !allSelected;
  
  // Render loading state
  if (loading) {
    return (
      <div className="w-full overflow-hidden border border-border rounded-lg">
        <div className="p-8 flex flex-col items-center justify-center">
          <Icon name="Loader" className="animate-spin text-primary mb-4" size={32} />
          <p className="text-text-secondary">Loading data...</p>
        </div>
      </div>
    );
  }
  
  // Render empty state
  if (filteredData.length === 0) {
    return (
      <div className="w-full overflow-hidden border border-border rounded-lg">
        <div className="p-8 flex flex-col items-center justify-center">
          <Icon name="FileX" className="text-text-tertiary mb-4" size={32} />
          <p className="text-text-secondary">{emptyMessage}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`w-full overflow-hidden border border-border rounded-lg ${className}`} {...props}>
      {filterable && (
        <div className="p-4 border-b border-border bg-surface flex flex-wrap gap-2">
          {columns
            .filter(column => column.filterable)
            .map(column => (
              <Dropdown
                key={column.key}
                variant="filter"
                placeholder={column.header}
                options={getUniqueValues(column.key)}
                selected={filters[column.key] || []}
                onChange={(values) => handleFilterChange(column.key, values)}
                width="w-auto"
                maxHeight="max-h-48"
              />
            ))}
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className={tableClasses[variant]}>
          <thead className="bg-surface">
            <tr>
              {selectable && (
                <th className="px-4 py-3 text-left">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAllRows(e.target.checked)}
                    />
                  </div>
                </th>
              )}
              
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-sm font-medium text-text-secondary tracking-wider ${
                    column.width ? column.width : ''
                  }`}
                >
                  <div className="flex items-center">
                    <span>{column.header}</span>
                    
                    {sortable && column.sortable && (
                      <button
                        className="ml-1 focus:outline-none"
                        onClick={() => requestSort(column.key)}
                      >
                        <Icon 
                          name={getSortDirectionIcon(column.key)} 
                          className="text-text-tertiary" 
                          size={16} 
                        />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-border">
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className={`${
                  onRowClick ? 'cursor-pointer hover:bg-surface' : ''
                } ${
                  selectedRows.includes(row.id) ? 'bg-primary bg-opacity-5' : ''
                }`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {selectable && (
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                      />
                    </div>
                  </td>
                )}
                
                {columns.map((column) => (
                  <td
                    key={`${row.id || rowIndex}-${column.key}`}
                    className="px-4 py-3 whitespace-nowrap"
                  >
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {pagination && totalPages > 0 && (
        <div className="px-4 py-3 border-t border-border bg-surface flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm text-text-secondary">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
            </span>
            
            <div className="ml-4">
              <select
                className="text-sm border border-border rounded-md bg-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                <option value="5">5 per page</option>
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              className="px-2 py-1 text-sm rounded-md border border-border bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronsLeft" size={16} />
            </button>
            
            <button
              className="px-2 py-1 text-sm rounded-md border border-border bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Show pages around current page
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === pageNum
                      ? 'bg-primary text-white' :'border border-border bg-white hover:bg-surface'
                  }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              className="px-2 py-1 text-sm rounded-md border border-border bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </button>
            
            <button
              className="px-2 py-1 text-sm rounded-md border border-border bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronsRight" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;